import {
    useCallback,
    useContext,
    useEffect,
    useState,
    createContext,
} from 'react';
import { Howl, Howler } from 'howler';
import { isSuspended, onSuspend } from '../lib/suspension';
import { createAudioLifecycle } from '../lib/audio-lifecycle';
import {
    createSoundPlayer,
    DEFAULT_SOUND_PLAYER,
    type BingoSounds,
    type SoundPlayer,
} from '../lib/sound-player';
import manifest from '../assets/audio.packer';

export { DEFAULT_SOUND_PLAYER };
export type { BingoSounds, SoundPlayer };

onSuspend(() => Howler.stop());

// Created at module load so Howler's idle auto-suspend is disabled before the
// first Howl creates the AudioContext.
const audioLifecycle = createAudioLifecycle({
    howler: Howler,
    document: typeof document !== 'undefined' ? document : undefined,
    isSuspended,
});

export type InitializationOptions = {
    loaded: boolean;
    sound: boolean;
    language: string;
    caller: string;
    announcer: string;
};

const createHowler = async (key: string) => {
    const audioManifest = manifest[key];

    if (!audioManifest) {
        return;
    }

    const response = await fetch(audioManifest.spriteFile);

    const sprite: Record<string, [number, number]> = await response.json();

    const howl = new Howl({
        src: audioManifest.audioFiles,
        sprite: sprite,
        mute: true,
    });
    audioLifecycle.track(howl);

    return howl;
};

const createHowlerWithFallbacks = async (keys: string[]) => {
    for (const key of keys) {
        const howler = await createHowler(key);
        if (howler) {
            return howler;
        }
    }
};

export const SoundContext = createContext<SoundPlayer | null>(null);

export const useSounds = () => useContext(SoundContext) as SoundPlayer;

const createLanguageSelection = (language: string) => {
    const languages = [language];
    const [prefix, suffix] = language.split('-');

    if (suffix) {
        languages.push(prefix);
    }

    return [...new Set(languages.concat('en'))];
};

export const useSoundInitialization = ({
    loaded,
    sound,
    language,
    caller,
    announcer,
}: InitializationOptions) => {
    const [sounds, setSounds] = useState<SoundPlayer>(DEFAULT_SOUND_PLAYER);

    useEffect(() => audioLifecycle.acquire(), []);

    useEffect(() => {
        if (!loaded) {
            return;
        }

        let unmounted = false;

        async function initializeSounds() {
            const languages = createLanguageSelection(language);
            const [sUI, sAnnouncer, sBall30, sBall75, sBall80, sBall90] =
                await Promise.all([
                    createHowler('sounds/ui'),
                    createHowlerWithFallbacks(
                        languages.map(
                            (lang) => `speech/${lang}/${announcer}/announcer`
                        )
                    ),
                    createHowlerWithFallbacks(
                        languages.map((lang) => `speech/${lang}/${caller}/30`)
                    ),
                    createHowlerWithFallbacks(
                        languages.map((lang) => `speech/${lang}/${caller}/75`)
                    ),
                    createHowlerWithFallbacks(
                        languages.map((lang) => `speech/${lang}/${caller}/80`)
                    ),
                    createHowlerWithFallbacks(
                        languages.map((lang) => `speech/${lang}/${caller}/90`)
                    ),
                ]);

            const howlers: BingoSounds = {
                ui: sUI,
                announcer: sAnnouncer,
                ball30: sBall30,
                ball75: sBall75,
                ball80: sBall80,
                ball90: sBall90,
            };

            if (unmounted || isSuspended()) {
                Object.values(howlers).forEach((howler) => howler?.unload());
                return;
            }

            setSounds(createSoundPlayer(howlers, audioLifecycle));
        }

        initializeSounds();

        return () => {
            unmounted = true;
        };
    }, [loaded, language, caller, announcer]);

    if (sounds.initialized) {
        sounds.mute(!sound);
    }

    return sounds;
};

export const useSoundHandler = <A, R>(
    callback: ((...args: A[]) => R) | undefined,
    id: string,
    key: keyof BingoSounds = 'ui'
) => {
    const sounds = useSounds();

    return useCallback(
        (...args: A[]) => {
            if (isSuspended()) return;
            sounds.play(key, id);
            if (callback) {
                return callback(...args);
            }
        },
        [callback, sounds, key, id]
    );
};
